import React, { useEffect, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useMutation } from '@apollo/client';

import { css } from '@emotion/react';
import { PURCHASE_STORY } from '@api/story';
import FullScreenPopupContainer from '../container/FullScreenPopupContainer';
import { useGetUserQuery } from '@/operations/queries/user/__generated__/getUser.generated';

import {
  payTicketAll,
  viewPurchaseStoryPopup,
  touchPurchaseStoryPopup,
  viewAllChapterPurchasePopup,
  payTicketForStory,
} from '@lib/eventManager';
import { Story_Play_Type } from '@/baseType';
import { useAppDispatch } from '@/store/hooks';
import { alertMessage, setToastMessage } from '@/slices/alertSlice';
import { SERVER_ERROR_CODE, STORAGE_KEY } from '@/common/values';

import NoticeWholePurchaseInfoPopup from '../NoticeWholePurchaseInfoPopup';
import { isMobileDevice } from '@/common/utils';
import {
  updateUserForApp,
  goAdultCertForApp,
  goStoreForApp,
  showToastForApp,
} from '@/lib/webview';
import { CoinProductFragment } from '@/operations/queries/story/__generated__/useGetStoryGameDetail.generated';
import StoryGamePCPopupContainer from './StoryGamePCPopupContainer';
import StoryGameMobilePopupContainer from './StoryGameMobilePopupContainer';
import theme from '@/styles/theme';
import Image from 'next/image';

interface IWholePurchaseInfoPopup {
  storyName?: string;
  storyId?: number;
  chapterIndex?: number;
  originalCoin?: number;
  lastPreviewChapterIndex?: number;
  storyCoin: number;
  isShow: boolean;
  onClose: () => void;
  storyBundleId?: number;
  storyLanguage?: string;
  storyGenre?: string;
  playType?: Story_Play_Type;
  coverImage?: string;
  discountRate: number;
  hasAdultChapter: boolean;
  coinProduct?: CoinProductFragment | null;
}

const StoryGameWholePurchaseInfoPopup = ({
  storyName,
  storyId,
  storyCoin,
  chapterIndex,
  originalCoin,
  lastPreviewChapterIndex,
  isShow,
  onClose,
  storyBundleId,
  storyLanguage,
  storyGenre,
  playType,
  coverImage,
  discountRate,
  hasAdultChapter,
  coinProduct,
}: IWholePurchaseInfoPopup) => {
  const { t } = useTranslation();
  const router = useRouter();
  const dispatch = useAppDispatch();

  const [
    showNoticeWholePurchaseInfoPopup,
    setShowNoticeWholePurchaseInfoPopup,
  ] = useState<boolean>(false);
  const { data: userData } = useGetUserQuery();

  const [purchaseStoryMutate] = useMutation<{
    purchaseStory: { ok: number };
  }>(PURCHASE_STORY, {
    onCompleted: (data) => {
      if (data.purchaseStory.ok === 1) {
        router.reload();
        isMobileDevice() && updateUserForApp();
        onClose();
        payTicketAll({
          storyId: storyId,
          ticketCount: storyCoin,
          storyNameAndChapterIndex: `${storyName}_${chapterIndex}`,
        });
        payTicketForStory({
          amount: storyCoin,
          storyId: storyId || 0,
          storyBundleId,
          storyName: storyName || '',
          storyLanguage,
          storyGenre,
          playType: 'storygame',
        });
        router.replace(`/story/${storyId}`);
      } else {
        onClose();
        dispatch(
          alertMessage({
            message: t('common_popup_description_error_occurred'),
            type: 'warning',
          }),
        );
      }
    },
    onError: (error) => {
      onClose();
      if (
        error.message.includes(SERVER_ERROR_CODE.ADULT_VERIFICATION_REQUIRED)
      ) {
        if (isMobileDevice()) {
          showToastForApp(t('payment_toast_all_chapter_19_chapter_included'));
          goAdultCertForApp({ callbackScreenType: 'story' });
        } else {
          dispatch(
            setToastMessage(t('payment_toast_all_chapter_19_chapter_included')),
          );

          const redirectInfo = JSON.stringify({
            redirectUrl: `/story/${storyId}`,
            createdAt: new Date(),
          });
          localStorage.setItem(STORAGE_KEY.REDIRECT_INFO, redirectInfo);

          router.replace({
            pathname: `/adult`,
            query: {
              url: `/story/${storyId}`,
              storyId,
            },
          });
        }
        return;
      }
      dispatch(setToastMessage(t('common_popup_description_error_occurred')));
    },
  });

  useEffect(() => {
    if (!isShow) return;

    viewPurchaseStoryPopup({
      storyName,
      storyId,
      fullPrice: originalCoin,
      price: storyCoin,
      userCoin: userData?.getUser?.currentCoin,
    });

    viewAllChapterPurchasePopup({
      storyName,
      storyId: String(storyId),
      fullCoin: originalCoin,
      coin: storyCoin,
      lastPreviewChapterIndex,
      purchasedChapter: storyCoin,
      contentType: 'interactive',
    });
  }, [isShow]);

  const onPurchase = async () => {
    touchPurchaseStoryPopup({
      storyName,
      storyId,
      fullPrice: originalCoin,
      price: storyCoin,
      userCoin: userData?.getUser?.currentCoin,
    });

    if (userData?.getUser?.isAnonymous && !coinProduct) {
      setShowNoticeWholePurchaseInfoPopup(true);
      return;
    }

    if (
      hasAdultChapter &&
      !userData?.getUser?.certificatedAt &&
      userData?.getUser?.isExpiredCertification
    ) {
      if (isMobileDevice()) {
        showToastForApp(t('payment_toast_all_chapter_19_chapter_included'));
        goAdultCertForApp({ callbackScreenType: 'story' });
      } else {
        dispatch(
          setToastMessage(t('payment_toast_all_chapter_19_chapter_included')),
        );
        const redirectInfo = JSON.stringify({
          redirectUrl: `/story/${storyId}`,
          createdAt: new Date(),
        });
        localStorage.setItem(STORAGE_KEY.REDIRECT_INFO, redirectInfo);

        await router.push({
          pathname: '/adult',
          query: {
            url: `/story/${storyId}`,
            storyId,
          },
        });
      }
      onClose();
      return;
    }

    if ((userData?.getUser?.currentCoin || 0) < storyCoin) {
      const redirectInfo = JSON.stringify({
        redirectUrl: `/story/${storyId}`,
        createdAt: new Date(),
      });
      if (isMobileDevice()) {
        goStoreForApp({ callbackScreenType: 'story' });
      } else {
        localStorage.setItem(STORAGE_KEY.REDIRECT_INFO, redirectInfo);

        // 통구매 작품 상품 정보가 있으면 payment로 바로 보내기
        if (coinProduct) {
          await router.push({
            pathname: '/payment',
            query: {
              inappCoinId: coinProduct.inappCoinId,
              storyProductId: storyId,
              url: `/story/${storyId}`,
            },
          });
        } else {
          await router.push({
            pathname: '/store',
            query: {
              coin: storyCoin,
              url: `/story/${storyId}`,
            },
          });
        }
      }
      onClose();
      return;
    }

    purchaseStoryMutate({
      variables: {
        storyId: storyId,
        price: storyCoin,
      },
    });
  };

  if (!isShow) {
    return null;
  }

  return (
    <FullScreenPopupContainer
      onClose={onClose}
      customCSS={popupWrapper}
      hasHeader={false}
    >
      <div css={backgroundImage}>
        <div className="background-image-wrapper">
          {coverImage && (
            <Image
              src={coverImage}
              layout="fill"
              objectFit="cover"
              alt="Full chat image"
              sizes="(max-width: 800px) 50vw, 33vw"
            />
          )}
          <div className="dimmed"></div>
        </div>
      </div>
      <div css={wrapper}>
        <div className="pc">
          <StoryGamePCPopupContainer
            storyName={storyName}
            storyCoin={storyCoin}
            originalCoin={originalCoin}
            onClose={onClose}
            coverImage={coverImage}
            discountRate={discountRate}
            coinProduct={coinProduct}
            onPurchase={onPurchase}
          />
        </div>
        <div className="mobile">
          <StoryGameMobilePopupContainer
            storyName={storyName}
            storyCoin={storyCoin}
            originalCoin={originalCoin}
            onClose={onClose}
            coverImage={coverImage}
            discountRate={discountRate}
            coinProduct={coinProduct}
            onPurchase={onPurchase}
          />
        </div>
      </div>
      <NoticeWholePurchaseInfoPopup
        isShow={showNoticeWholePurchaseInfoPopup}
        storyId={storyId}
        onClose={() => {
          onClose();
          setShowNoticeWholePurchaseInfoPopup(false);
        }}
      />
    </FullScreenPopupContainer>
  );
};

const wrapper = css`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  .pc {
    display: block;
  }
  .mobile {
    display: none;
    width: 100%;
  }

  @media screen and (max-width: ${theme.devices.mobile}) {
    .pc {
      display: none;
    }
    .mobile {
      display: block;
    }
  }
`;
const backgroundImage = css`
  position: absolute;
  cursor: pointer;
  object-fit: fill;
  width: 100vw;
  height: 100%;
  top: 0;
  background: black;

  .background-image-wrapper {
    position: absolute;
    width: 100vw;
    height: calc(100vw * 9 / 16);
    opacity: 0.25;
  }

  .dimmed {
    position: absolute;
    top: 0;
    width: 100vw;
    height: calc(100vw * 9 / 16);
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000 57.01%);
    backdrop-filter: blur(8px);
  }
`;

const popupWrapper = css`
  width: 100vw;
  display: flex;
  justify-content: center;

  .header {
    width: 100%;
    padding: initial;
    height: 3rem;
    display: flex;
    align-items: center;
    padding: 0.75rem 1.5rem;
    position: relative;

    .back-icon {
      position: absolute;
      left: 1.5rem;
      top: 0.75rem;
    }
  }
`;

export default StoryGameWholePurchaseInfoPopup;
